
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentService } from '../../services/studentService';
import { supabase } from '../../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AppIcon from '../../components/AppIcon';
import AdminLayout from '../../components/layouts/AdminLayout'; // Import Layout
import StudentForm from './StudentForm';
import * as enrollmentService from '../../services/enrollmentService';
import { getStudentFeeStatementSummary } from '../../services/feeService';

const StudentDetail = ({ embedded = false, studentId, onBack }) => {
    const { id: routeId } = useParams();
    const id = studentId || routeId;
    const navigate = useNavigate();

    const calculateStage = (moduleNum) => {
        const mod = Number(moduleNum) || 1;
        const year = Math.ceil(mod / 2);
        const sem = mod % 2 === 0 ? 2 : 1;
        return `Y${year}M${sem}`;
    };
    const [student, setStudent] = useState(null);
    const [feeBalance, setFeeBalance] = useState(null);
    const [feeSummary, setFeeSummary] = useState(null);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStudent();
    }, [id]);

    const loadStudent = async () => {
        try {
            const data = await studentService.getStudentById(id);
            if (!data) {
                setStudent(null);
                return;
            }

            // Repair a stale profile/enrollment module before rendering the
            // stage card. This is important when the new semester was opened
            // before the final released result advanced the learner.
            const progression = await enrollmentService.syncStudentModuleProgression(id);
            if (!progression.success) console.warn('Module progression sync was skipped:', progression.error);
            const { data: liveEnrollment } = await supabase
                .from('student_semester_enrollments')
                .select('module_number')
                .eq('student_id', id)
                .in('status', ['reported', 'active', 'registration_completed', 'billed'])
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            const currentModule = Math.max(
                Number(progression.data?.current_module || 0),
                Number(liveEnrollment?.module_number || 0),
                Number(data.current_module || data.starting_module || 1),
                1
            );
            const resolvedStudent = currentModule !== Number(data.current_module || data.starting_module || 1)
                ? { ...data, current_module: currentModule }
                : data;
            setStudent(resolvedStudent);
            // The fee statement is the financial source of truth. Use the
            // profile ID first because ledger and payment rows are posted
            // against that identity, and retain signed balances so an
            // overpayment is shown as a credit rather than as an outstanding
            // charge.
            const summary = await getStudentFeeStatementSummary(resolvedStudent);
            setFeeSummary(summary);
            setFeeBalance(summary.balance);
        } catch (error) {
            console.error("Failed to load student", error);
            setFeeSummary(null);
            setFeeBalance(null);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-10 text-center">Loading student details...</div>;
    if (!student) return <div className="p-10 text-center text-red-500">Student not found.</div>;

    const Layout = embedded ? React.Fragment : ({ children }) => <AdminLayout title="Student Details">{children}</AdminLayout>;
    if (editing) return <StudentForm embedded studentId={id} onBack={() => setEditing(false)} />;
    return (
        <Layout>
            <div className="max-w-6xl mx-auto p-6 space-y-6">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center space-x-4">
                        <Button variant="ghost" onClick={() => onBack ? onBack() : navigate('/students')}>
                            <AppIcon name="arrow-left" className="mr-2 h-4 w-4" />
                            Back
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold">{student.first_name} {student.last_name}</h1>
                            <div className="flex items-center space-x-2 text-muted-foreground mt-1">
                                <span>{student.admission_number || 'Pending Admission'}</span>
                                <span>•</span>
                                <span>{student.institutional_email || student.email}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex space-x-2">
                        <Button variant="outline" onClick={() => setEditing(true)}>
                            <AppIcon name="edit" className="mr-2 h-4 w-4" />
                            Edit Profile
                        </Button>
                        <Button variant="destructive">
                            Delete
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Left Column: Personal Info */}
                    <Card className="md:col-span-1">
                        <CardHeader>
                            <CardTitle>Personal Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Full Name</label>
                                <p>{student.first_name} {student.middle_name} {student.last_name}</p>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Contact</label>
                                <p>{student.phone_number || '-'}</p>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Official student email</label>
                                <p>{student.institutional_email || student.email || '-'}</p>
                            </div>
                            {student.personal_email && student.personal_email !== (student.institutional_email || student.email) && (
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Backup personal email</label>
                                    <p>{student.personal_email}</p>
                                    <p className="text-xs text-gray-500">Retained from the admission application.</p>
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Demographics</label>
                                <p>Gender: {student.gender || '-'}</p>
                                <p>DOB: {student.date_of_birth || '-'}</p>
                                <p>Nation: {student.nationality || '-'}</p>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">ID / Passport</label>
                                <p>{student.id_number || '-'}</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Middle Column: Academic Info */}
                    <Card className="md:col-span-2">
                        <CardHeader>
                            <CardTitle>Academic Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Program & Level</label>
                                    <p className="font-medium text-lg">{student.program?.name || '-'}</p>
                                    <p className="text-sm font-semibold text-blue-600">{student.academic_level || 'N/A'}</p>
                                    <p className="text-xs text-gray-400">{student.program?.code}</p>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Intake</label>
                                    <p className="font-medium text-lg">{student.intake_code}</p>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Current Stage</label>
                                    <p className="font-bold text-2xl text-blue-600 mt-1">{calculateStage(student.current_module)}</p>
                                    <p className="text-[10px] text-gray-400 uppercase">System Module: {student.current_module}</p>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Academic Status</label>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                                            {student.current_module}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium">Progress: Mod {student.current_module}</span>
                                            <span className="text-[10px] text-gray-500">Starting point: Mod {student.starting_module} ({calculateStage(student.starting_module)})</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
                                    <div className="mt-1">
                                        <span className={`px-3 py-1 rounded-full text-sm font-semibold 
                                            ${student.status?.toLowerCase() === 'active' ? 'bg-green-100 text-green-800' :
                                                student.status?.toLowerCase() === 'admitted' ? 'bg-blue-100 text-blue-800' :
                                                    student.status?.toLowerCase() === 'inactive' ? 'bg-amber-100 text-amber-800' :
                                                        student.status?.toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            ['suspended', 'withdrawn'].includes(student.status?.toLowerCase()) ? 'bg-red-100 text-red-800' :
                                                                'bg-gray-100 text-gray-800'}`}>
                                            {student.status || 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t">
                                <h4 className="font-semibold mb-4">Documents</h4>
                                {student.documents && student.documents.length > 0 ? (
                                    <ul className="space-y-2">
                                        {student.documents.map(doc => (
                                            <li key={doc.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                                                <div className="flex items-center">
                                                    <AppIcon name="file-text" className="h-4 w-4 mr-2 text-gray-500" />
                                                    <span className="text-sm font-medium">{doc.document_name}</span>
                                                    <span className="text-xs text-gray-500 ml-2">({doc.document_type})</span>
                                                </div>
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs hover:underline">View</a>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-500">No documents uploaded.</p>
                                )}
                                <div className="mt-4 flex items-center space-x-2">
                                    <select
                                        className="border rounded px-2 py-1 text-sm bg-white"
                                        id="docTypeSelect"
                                    >
                                        <option value="Transcript">Transcript</option>
                                        <option value="Certificate">Certificate</option>
                                        <option value="ID Copy">ID Copy</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <div className="relative">
                                        <input
                                            type="file"
                                            className="hidden"
                                            id="fileInput"
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;

                                                const docType = document.getElementById('docTypeSelect').value;
                                                if (!confirm(`Upload ${file.name} as ${docType}?`)) return;

                                                try {
                                                    const res = await studentService.uploadDocument(student.profile_id, file, docType);
                                                    if (res.success) {
                                                        alert('Document uploaded successfully!');
                                                        window.location.reload(); // Simple reload to refresh list
                                                    } else {
                                                        alert('Upload failed: ' + res.error);
                                                    }
                                                } catch (err) {
                                                    console.error(err);
                                                    alert('Upload error.');
                                                }
                                            }}
                                        />
                                        <Button size="sm" variant="outline" onClick={() => document.getElementById('fileInput').click()}>
                                            <AppIcon name="upload" className="mr-2 h-4 w-4" />
                                            Upload
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader><CardTitle>Academic Progress</CardTitle></CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4">
                            <div><label className="text-xs font-semibold uppercase text-gray-500">Current module</label><p className="text-xl font-semibold">Module {student.current_module || '—'}</p></div>
                            <div><label className="text-xs font-semibold uppercase text-gray-500">Academic standing</label><p className="text-xl font-semibold capitalize">{student.academic_standing || 'Not recorded'}</p></div>
                            <div><label className="text-xs font-semibold uppercase text-gray-500">Level</label><p>{student.academic_level || 'Not recorded'}</p></div>
                            <div><label className="text-xs font-semibold uppercase text-gray-500">Enrollment date</label><p>{student.enrollment_date || 'Not recorded'}</p></div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader><CardTitle>Fees and Finance</CardTitle></CardHeader>
                        <CardContent>
                            {feeSummary && !feeSummary.error && feeBalance != null && feeSummary.hasTransactions ? (
                                <>
                                    <label className="text-xs font-semibold uppercase text-gray-500">
                                        {feeBalance > 0 ? 'Outstanding balance' : feeBalance < 0 ? 'Credit balance (overpayment)' : 'Fee balance'}
                                    </label>
                                    <p className={`mt-1 text-2xl font-bold ${feeBalance < 0 ? 'text-emerald-700' : ''}`}>
                                        KES {Math.abs(Number(feeBalance)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </p>
                                    {feeBalance === 0 && <p className="mt-1 text-sm text-emerald-700">Paid in full</p>}
                                    {feeBalance < 0 && <p className="mt-1 text-sm text-emerald-700">Available credit on the fee statement</p>}
                                </>
                            ) : feeSummary && !feeSummary.error ? (
                                <div className="rounded-lg border border-dashed p-6 text-center"><AppIcon name="wallet" className="mx-auto mb-2 h-6 w-6 text-gray-400" /><p className="font-medium">No fee transactions recorded</p></div>
                            ) : <div className="rounded-lg border border-dashed p-6 text-center"><AppIcon name="wallet" className="mx-auto mb-2 h-6 w-6 text-gray-400" /><p className="font-medium">Fee statement unavailable</p></div>}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};

export default StudentDetail;
